<template>
  <q-table
    :rows="accessRows"
    :columns="columns"
    :pagination="{
      rowsPerPage: 10
    }"
    row-key="id"
    dense
    flat
    bordered
    :table-header-class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
  >
    <template #body-cell-name="props">
      <q-td :props="props">
        <div class="row q-gutter-x-sm">
          <router-link
            :to="{ name: 'Property Report', params: { reportId: props.row.id } }"
            class="property-link"
          >
            <div class="text-bold">{{ props.value }}</div>
            <div v-if="props.row.subject" class="text-weight-regular">
              {{ props.row.subject.street_address }}
            </div>
            <div v-if="props.row.subject" class="text-weight-regular">
              {{ props.row.subject.city }}, {{ props.row.subject.state }}
              {{ props.row.subject.zip_code }}
            </div>
          </router-link>
          <div class="q-my-auto">
            <q-icon v-if="props.row.public_id" name="public">
              <q-tooltip>Public</q-tooltip>
            </q-icon>
            <q-icon v-else-if="props.row.is_organization_visible" name="business">
              <q-tooltip v-if="organizationName !== undefined">
                {{ organizationName }}
              </q-tooltip>
              <q-tooltip v-else>No Organization</q-tooltip>
            </q-icon>
          </div>
        </div>
      </q-td>
    </template>
    <template #body-cell-user_id="props">
      <q-td :props="props">
        <div v-if="props.value" class="flex">
          <q-avatar size="28px" round class="bg-grey-4">
            <q-img
              v-if="props.value?.picture"
              :src="props.value.picture"
              referrerpolicy="no-referrer"
              no-spinner
            />
            <q-icon v-else name="person" />
          </q-avatar>
          <div v-if="$q.screen.gt.md" class="q-ml-sm q-my-auto">
            {{ userToName(props.value) }}
          </div>
          <q-tooltip v-else>{{ userToName(props.value) }}</q-tooltip>
        </div>
      </q-td>
    </template>
    <template #body-cell-actions="props">
      <q-td :props="props">
        <q-btn round dense unelevated @click="removeEditReportAccess(props.row.id)">
          <q-icon name="delete" size="medium" color="red" />
        </q-btn>
      </q-td>
    </template>
  </q-table>
</template>

<script lang="ts">
import { useUserStore } from '@/stores/user'
import { type RentSourceSubscriptionAttributes } from 'common'
import { userToName } from '@/utils'
import { usePropertyReportStore } from '@/stores/propertyreport'
import { parseISO, formatDistance } from 'date-fns'

export default {
  name: 'MiniPropertyReportsTable',
  props: {
    reportIds: {
      required: true,
      type: Array<number>
    }
  },
  emits: ['remove'],
  data() {
    return {
      userToName,
      formatDistance,
      parseISO,
      columns: [
        {
          field: 'name',
          name: 'name',
          label: 'Report Name',
          sortable: true,
          align: 'left'
        },
        {
          field: (row: RentSourceSubscriptionAttributes) => {
            return this.getUser(row.user_id)
          },
          name: 'user_id',
          label: 'Owner',
          sortable: true,
          align: 'left'
        },
        {
          field: (row: RentSourceSubscriptionAttributes) => {
            return formatDistance(parseISO(row.modified_on), new Date(), {
              addSuffix: true
            })
          },
          name: 'modified_on',
          label: 'Last Modified',
          sortable: true,
          align: 'left'
        },

        {
          name: 'actions',
          label: 'Actions',
          field: 'actions'
        }
      ]
    }
  },
  computed: {
    accessRows() {
      return this.reportIds
        .map((id) => this.activeSubscriptions.find((_) => _.id === id))
        .filter((_) => _)
    },
    activeSubscriptions() {
      return usePropertyReportStore().activeAdminSubscriptions
    },
    organizationName() {
      return useUserStore().organization?.display_name
    }
  },
  methods: {
    removeEditReportAccess(id: number) {
      this.$emit('remove', id)
    },
    getUser(id: string) {
      return useUserStore().peopleInOrgOrSharedPerId.get(id)
    }
  }
}
</script>
